import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { ToasterComponent } from './toaster.component';
import { isPlatformBrowser } from '@angular/common';

export type ToasterType = 'success' | 'error' | 'warning' | 'info';

export interface ToasterData {
  message: string;
  type: ToasterType;
  duration?: number;
  showCloseButton?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  private readonly defaultDuration = 4000;
  private readonly maxZIndex = 2147483647;
  private styleInjected = false;

  constructor(
    private snackBar: MatSnackBar,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.ensureToasterPriority();
  }

  private ensureToasterPriority(): void {
    if (!isPlatformBrowser(this.platformId) || this.styleInjected) {
      return;
    }

    try {
      const style = document.createElement('style');
      style.textContent = `
        /* Toaster positioning and z-index management */
        .custom-toaster,
        .mat-mdc-snack-bar-container.custom-toaster,
        .cdk-overlay-pane:has(.custom-toaster) {
          z-index: ${this.maxZIndex} !important;
          position: fixed !important;
        }
        .toaster-right-positioned,
        .mat-mdc-snack-bar-container.toaster-right-positioned,
        .cdk-overlay-pane.toaster-right-positioned {
          right: 20px !important;
          left: auto !important;
          bottom: 20px !important;
          position: fixed !important;
          transform: none !important;
        }

        /* Toaster type-specific styles with proper width and colors */
        .mat-mdc-snack-bar-container.toaster-success,
        .mat-mdc-snackbar-surface.toaster-success {
          background: var(--toaster-success-bg) !important;
          color: var(--toaster-success-text) !important;
          border-radius: 12px !important;
          width: 496px !important;
          min-width: 272px !important;
          max-width: 90vw !important;
          box-shadow: none !important;
          padding: 0 !important;
        }

        .mat-mdc-snack-bar-container.toaster-info,
        .mat-mdc-snackbar-surface.toaster-info {
          background: var(--toaster-info-bg) !important;
          color: var(--toaster-info-text) !important;
          border-radius: 12px !important;
          width: 496px !important;
          min-width: 272px !important;
          max-width: 90vw !important;
          box-shadow: none !important;
          padding: 0 !important;
        }

        .mat-mdc-snack-bar-container.toaster-warning,
        .mat-mdc-snackbar-surface.toaster-warning {
          background: var(--toaster-warning-bg) !important;
          color: var(--toaster-warning-text) !important;
          border-radius: 12px !important;
          width: 496px !important;
          min-width: 272px !important;
          max-width: 90vw !important;
          box-shadow: none !important;
          padding: 0 !important;
        }

        .mat-mdc-snack-bar-container.toaster-error,
        .mat-mdc-snackbar-surface.toaster-error {
          background: var(--toaster-error-bg) !important;
          color: var(--toaster-error-text) !important;
          border-radius: 12px !important;
          width: 496px !important;
          min-width: 272px !important;
          max-width: 90vw !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
      `;
      document.head.appendChild(style);
      this.styleInjected = true;
    } catch (error) {
      console.warn('Failed to inject toaster styles:', error);
    }
  }

  show(
    message: string,
    duration: number = this.defaultDuration,
    type: ToasterType = 'info',
    showCloseButton: boolean = true,
  ): MatSnackBarRef<ToasterComponent> | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const data: ToasterData = {
        message,
        type,
        duration,
        showCloseButton,
      };

      // Dismiss any existing snackbar
      this.snackBar.dismiss();

      const snackBarRef = this.snackBar.openFromComponent(ToasterComponent, {
        data,
        duration: duration,
        panelClass: [
          `toaster-${type}`,
          'custom-toaster',
          'toaster-high-z',
          'toaster-right-positioned',
          'toaster-scoped',
        ],
        horizontalPosition: 'end',
        verticalPosition: 'bottom',
      });

      // Force z-index and right positioning after a brief delay to ensure DOM is ready
      setTimeout(() => {
        const containerElements = document.querySelectorAll(
          '.custom-toaster, .mat-mdc-snack-bar-container, .toaster-right-positioned, .cdk-overlay-pane',
        );
        containerElements.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.style.zIndex = this.maxZIndex.toString();
            element.style.right = '20px';
            element.style.left = 'auto';
            element.style.bottom = '20px';
            element.style.position = 'fixed';
            element.style.transform = 'none';
          }
        });
      }, 50);

      return snackBarRef;
    } catch (error) {
      console.error('Failed to show toaster:', error);
      return null;
    }
  }

  success(
    message: string,
    duration: number = this.defaultDuration,
  ): MatSnackBarRef<ToasterComponent> | null {
    return this.show(message, duration, 'success');
  }

  error(
    message: string,
    duration: number = this.defaultDuration,
  ): MatSnackBarRef<ToasterComponent> | null {
    return this.show(message, duration, 'error');
  }

  warning(
    message: string,
    duration: number = this.defaultDuration,
  ): MatSnackBarRef<ToasterComponent> | null {
    return this.show(message, duration, 'warning');
  }

  info(
    message: string,
    duration: number = this.defaultDuration,
  ): MatSnackBarRef<ToasterComponent> | null {
    return this.show(message, duration, 'info');
  }

  dismissAll(): void {
    this.snackBar.dismiss();
  }
}
